import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { IconPlus, IconEdit, IconTrash, IconFileInvoice, IconArrowDownRight, IconChevronDown, IconChevronUp, IconArrowBackUp } from '@tabler/icons';
import { useHistory } from 'react-router-dom';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';

const formatCurrencyBR = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const formatDateBR = (value) => {
    if (!value) return '-';
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
};

const STATUS_LABEL = {
    draft: { label: 'Rascunho', color: 'default' },
    open: { label: 'Em aberto', color: 'default' },
    partial: { label: 'Parcial', color: 'warning' },
    overdue: { label: 'Vencida', color: 'error' },
    paid: { label: 'Recebida', color: 'success' }
};

const emptyReceiptForm = {
    amount: '',
    discount_amount: '',
    interest_amount: '',
    fine_amount: '',
    payment_date: new Date().toISOString().slice(0, 10),
    bank_account: '',
    notes: ''
};

const ViewAllAccountsReceivable = () => {
    const history = useHistory();

    const [appId, setAppId] = useState(null);
    const [receivables, setReceivables] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [expanded, setExpanded] = useState({});
    const [bankAccounts, setBankAccounts] = useState([]);

    const [receiptDialog, setReceiptDialog] = useState(null); // { receivableId, installment }
    const [receiptForm, setReceiptForm] = useState(emptyReceiptForm);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        listarContas();
        (async () => {
            const defaultRes = await api.get('v1/apps/default/');
            const res = await api.get(`v1/apps/${appId}/bank-accounts/?company=${defaultRes.data.default_company_id}`);
            setBankAccounts(res.data.bank_accounts || []);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, statusFilter]);

    const listarContas = async () => {
        const query = statusFilter ? `?status=${statusFilter}` : '';
        const res = await api.get(`v1/apps/${appId}/receivable/${query}`);
        setReceivables(res.data.receivables || []);
    };

    const refreshReceivable = async (receivableId) => {
        const res = await api.get(`v1/apps/${appId}/receivable/${receivableId}/`);
        setReceivables((prev) => prev.map((r) => (r.id === receivableId ? res.data : r)));
    };

    const toggleExpand = (receivableId) => {
        setExpanded((prev) => ({ ...prev, [receivableId]: !prev[receivableId] }));
    };

    const handleDelete = async (receivable) => {
        if (!window.confirm(`Excluir a conta "${receivable.description}"? Isso remove todas as parcelas e recebimentos dela.`)) return;
        await api.delete(`v1/apps/${appId}/receivable/${receivable.id}/`);
        setReceivables((prev) => prev.filter((r) => r.id !== receivable.id));
    };

    const handleOpenReceiptDialog = (receivableId, installment) => {
        setReceiptDialog({ receivableId, installment });
        setReceiptForm({ ...emptyReceiptForm, amount: installment.remaining_balance });
    };

    const handleRegisterReceipt = async () => {
        if (!receiptDialog) return;
        setSaving(true);
        try {
            await api.post(`v1/apps/${appId}/receivable-installments/${receiptDialog.installment.id}/receipts/`, {
                amount: receiptForm.amount || 0,
                discount_amount: receiptForm.discount_amount || 0,
                interest_amount: receiptForm.interest_amount || 0,
                fine_amount: receiptForm.fine_amount || 0,
                payment_date: receiptForm.payment_date,
                bank_account: receiptForm.bank_account || null,
                notes: receiptForm.notes || null
            });
            await refreshReceivable(receiptDialog.receivableId);
            setReceiptDialog(null);
        } catch (error) {
            console.error(error);
            window.alert(error.response?.data?.error || 'Erro ao registrar recebimento');
        } finally {
            setSaving(false);
        }
    };

    const handleReverseReceipt = async (receivableId, receipt) => {
        if (!window.confirm('Estornar esse recebimento?')) return;
        await api.post(`v1/apps/${appId}/receipts/${receipt.id}/reverse/`, {});
        await refreshReceivable(receivableId);
    };

    const handleReceipt = async (receipt) => {
        const res = await api.get(`v1/apps/${appId}/receipts/${receipt.id}/receipt/`, { responseType: 'blob' });
        const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        window.open(blobUrl, '_blank');
    };

    const total = receivables.reduce((acc, r) => acc + Number(r.remaining_balance), 0);

    return (
        <Box>
            <PageHeader
                icon={IconArrowDownRight}
                gradient="teal"
                title="Contas a Receber"
                subtitle={`Total em aberto: ${formatCurrencyBR(total)}`}
                actions={
                    <Stack direction="row" spacing={2}>
                        <TextField
                            select
                            size="small"
                            label="Status"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            sx={{ width: 160 }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="open">Em aberto</MenuItem>
                            <MenuItem value="partial">Parcial</MenuItem>
                            <MenuItem value="overdue">Vencida</MenuItem>
                            <MenuItem value="paid">Recebida</MenuItem>
                        </TextField>

                        <Button
                            variant="contained"
                            startIcon={<IconPlus size={18} />}
                            onClick={() => history.push('/dashboard/financial/receivable/new')}
                        >
                            Nova Conta
                        </Button>
                    </Stack>
                }
            />

            <Card>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" />
                                <TableCell>Descrição</TableCell>
                                <TableCell>Cliente</TableCell>
                                <TableCell align="right">Total</TableCell>
                                <TableCell align="right">Saldo</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {receivables.map((receivable) => {
                                const statusInfo = STATUS_LABEL[receivable.status] || { label: receivable.status, color: 'default' };
                                const isExpanded = !!expanded[receivable.id];
                                return (
                                    <React.Fragment key={receivable.id}>
                                        <TableRow hover>
                                            <TableCell padding="checkbox">
                                                <IconButton size="small" onClick={() => toggleExpand(receivable.id)}>
                                                    {isExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>{receivable.description}</TableCell>
                                            <TableCell>{receivable.organization_name}</TableCell>
                                            <TableCell align="right">{formatCurrencyBR(receivable.total_amount)}</TableCell>
                                            <TableCell align="right">{formatCurrencyBR(receivable.remaining_balance)}</TableCell>
                                            <TableCell>
                                                <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => history.push(`/dashboard/financial/receivable/${receivable.id}/edit`)}
                                                >
                                                    <IconEdit size={16} />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => handleDelete(receivable)}>
                                                    <IconTrash size={16} />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                    <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, my: 1 }}>
                                                        <Typography variant="subtitle2" fontWeight={700} mb={1}>
                                                            Parcelas
                                                        </Typography>
                                                        {receivable.installments.map((installment) => {
                                                            const instStatus = STATUS_LABEL[installment.status] || {
                                                                label: installment.status,
                                                                color: 'default'
                                                            };
                                                            return (
                                                                <Box
                                                                    key={installment.id}
                                                                    sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
                                                                >
                                                                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                                                                        <Typography variant="body2" fontWeight={600}>
                                                                            Parcela {installment.number}/{installment.total_installments}
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            Valor: {formatCurrencyBR(installment.amount)}
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            Vencimento: {formatDateBR(installment.due_date)}
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            Saldo: {formatCurrencyBR(installment.remaining_balance)}
                                                                        </Typography>
                                                                        <Chip label={instStatus.label} color={instStatus.color} size="small" />
                                                                        {Number(installment.remaining_balance) > 0 && (
                                                                            <Button
                                                                                size="small"
                                                                                variant="outlined"
                                                                                onClick={() => handleOpenReceiptDialog(receivable.id, installment)}
                                                                            >
                                                                                Registrar recebimento
                                                                            </Button>
                                                                        )}
                                                                    </Stack>

                                                                    {installment.receipts.length > 0 && (
                                                                        <Table size="small" sx={{ mt: 1 }}>
                                                                            <TableHead>
                                                                                <TableRow>
                                                                                    <TableCell>Data</TableCell>
                                                                                    <TableCell align="right">Valor</TableCell>
                                                                                    <TableCell align="right">Juros</TableCell>
                                                                                    <TableCell align="right">Multa</TableCell>
                                                                                    <TableCell align="right">Desconto</TableCell>
                                                                                    <TableCell align="right" />
                                                                                </TableRow>
                                                                            </TableHead>
                                                                            <TableBody>
                                                                                {installment.receipts.map((receipt) => (
                                                                                    <TableRow key={receipt.id}>
                                                                                        <TableCell
                                                                                            sx={{
                                                                                                textDecoration: receipt.is_reversed
                                                                                                    ? 'line-through'
                                                                                                    : 'none'
                                                                                            }}
                                                                                        >
                                                                                            {formatDateBR(receipt.payment_date)}
                                                                                            {receipt.is_reversed && ' (estornado)'}
                                                                                        </TableCell>
                                                                                        <TableCell align="right">
                                                                                            {formatCurrencyBR(receipt.amount)}
                                                                                        </TableCell>
                                                                                        <TableCell align="right">
                                                                                            {formatCurrencyBR(receipt.interest_amount)}
                                                                                        </TableCell>
                                                                                        <TableCell align="right">
                                                                                            {formatCurrencyBR(receipt.fine_amount)}
                                                                                        </TableCell>
                                                                                        <TableCell align="right">
                                                                                            {formatCurrencyBR(receipt.discount_amount)}
                                                                                        </TableCell>
                                                                                        <TableCell align="right">
                                                                                            <Tooltip title="Ver recibo">
                                                                                                <IconButton
                                                                                                    size="small"
                                                                                                    onClick={() => handleReceipt(receipt)}
                                                                                                >
                                                                                                    <IconFileInvoice size={14} />
                                                                                                </IconButton>
                                                                                            </Tooltip>
                                                                                            {!receipt.is_reversed && (
                                                                                                <Tooltip title="Estornar">
                                                                                                    <IconButton
                                                                                                        size="small"
                                                                                                        onClick={() =>
                                                                                                            handleReverseReceipt(
                                                                                                                receivable.id,
                                                                                                                receipt
                                                                                                            )
                                                                                                        }
                                                                                                    >
                                                                                                        <IconArrowBackUp size={14} />
                                                                                                    </IconButton>
                                                                                                </Tooltip>
                                                                                            )}
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                ))}
                                                                            </TableBody>
                                                                        </Table>
                                                                    )}
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                            {receivables.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        Nenhuma conta encontrada
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Dialog open={!!receiptDialog} onClose={() => setReceiptDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Registrar recebimento
                    {receiptDialog && ` - Parcela ${receiptDialog.installment.number}/${receiptDialog.installment.total_installments}`}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Valor recebido"
                                type="number"
                                fullWidth
                                inputProps={{ min: 0, step: '0.01' }}
                                value={receiptForm.amount}
                                onChange={(e) => setReceiptForm({ ...receiptForm, amount: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Data do recebimento"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={receiptForm.payment_date}
                                onChange={(e) => setReceiptForm({ ...receiptForm, payment_date: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Juros"
                                type="number"
                                fullWidth
                                inputProps={{ min: 0, step: '0.01' }}
                                value={receiptForm.interest_amount}
                                onChange={(e) => setReceiptForm({ ...receiptForm, interest_amount: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Multa"
                                type="number"
                                fullWidth
                                inputProps={{ min: 0, step: '0.01' }}
                                value={receiptForm.fine_amount}
                                onChange={(e) => setReceiptForm({ ...receiptForm, fine_amount: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Desconto"
                                type="number"
                                fullWidth
                                inputProps={{ min: 0, step: '0.01' }}
                                value={receiptForm.discount_amount}
                                onChange={(e) => setReceiptForm({ ...receiptForm, discount_amount: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                label="Conta Bancária (opcional)"
                                fullWidth
                                value={receiptForm.bank_account}
                                onChange={(e) => setReceiptForm({ ...receiptForm, bank_account: e.target.value })}
                            >
                                <MenuItem value="">Nenhuma</MenuItem>
                                {bankAccounts.map((ba) => (
                                    <MenuItem key={ba.id} value={ba.id}>
                                        {ba.bank_name} - Ag {ba.agency_number} - CC {ba.account_number}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Notas"
                                fullWidth
                                multiline
                                minRows={2}
                                value={receiptForm.notes}
                                onChange={(e) => setReceiptForm({ ...receiptForm, notes: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReceiptDialog(null)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleRegisterReceipt} disabled={saving}>
                        {saving ? 'Salvando...' : 'Registrar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ViewAllAccountsReceivable;
